"use client";

import { useEffect, useState } from "react";
import { MessageCircle, Phone, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Magnetic } from "@/components/ui/magnetic";

const SESSION_KEY = "reading-fatigue-popup-shown";

interface ReadingFatiguePopupProps {
  // Shown in the prefilled WhatsApp message so the chat opens with context,
  // e.g. "Flutter developer in Delhi".
  contextLabel: string;
  // Passed in as a plain string (not imported from lib/data here) - this is
  // a client component, and importing CONTACT_DATA directly would pull the
  // whole lib/data.ts module (PROJECTS_DATA, ABOUT_DATA, etc.) into the
  // client bundle for every hire/best page, the same class of bug fixed
  // earlier for the blog-content barrel export.
  whatsappNumber: string;
}

// A one-time-per-tab-session nudge for visitors reading a long hire/best
// page: appears after a random 5-10s delay, offers a faster path (WhatsApp
// or a call) than reading the rest of the page. Centered over a dismissible
// backdrop (click outside or the X to close) - small card, not full-bleed,
// so it doesn't read as an aggressive interstitial. Plain CSS transition,
// not framer-motion - this
// component renders on every hire/best page, and AnimatePresence isn't
// covered by the app-wide LazyMotion bundle, so it was adding ~23kB of
// duplicated per-route JS across exactly these two page templates.
export function ReadingFatiguePopup({ contextLabel, whatsappNumber }: ReadingFatiguePopupProps) {
  const [mounted, setMounted] = useState(false);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(SESSION_KEY)) return;

    const delay = 5000 + Math.random() * 5000; // random, 5-10s
    const timer = setTimeout(() => {
      setMounted(true);
      sessionStorage.setItem(SESSION_KEY, "1");
      // Mount first with the entry transform, then flip a frame later so
      // the CSS transition actually animates instead of snapping in.
      requestAnimationFrame(() => setEntered(true));
    }, delay);

    return () => clearTimeout(timer);
  }, []);

  const dismiss = () => {
    setEntered(false);
    setTimeout(() => setMounted(false), 200);
  };

  if (!mounted) return null;

  const whatsappMessage = encodeURIComponent(
    `Hi Sachin, I was reading your ${contextLabel} page and wanted to talk directly.`
  );

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 transition-opacity duration-200 ${
        entered ? "opacity-100" : "opacity-0"
      }`}
      onClick={dismiss}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`relative w-full max-w-md bg-background border rounded-3xl shadow-2xl p-8 space-y-4 transition-all duration-200 ${
          entered ? "opacity-100 scale-100" : "opacity-0 scale-95"
        }`}
      >
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          className="absolute top-5 right-5 text-muted-foreground hover:text-foreground transition-colors"
        >
          <X size={18} />
        </button>
        <p className="font-semibold text-lg pr-6">Tired of reading? 👀</p>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Skip the rest of the page — message me on WhatsApp or just call.
          Didn&apos;t pick up? I&apos;ll call you back soon.
        </p>
        <div className="flex flex-col gap-3 pt-2">
          <Magnetic>
            <a
              href={`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-3.5 rounded-full border border-emerald-500/20 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-500 font-bold text-sm transition-colors"
            >
              <MessageCircle size={16} />
              Message on WhatsApp
            </a>
          </Magnetic>
          <Magnetic>
            <a href={`tel:+${whatsappNumber}`}>
              <Button size="lg" variant="outline" className="w-full gap-2 rounded-full">
                <Phone size={14} />
                Call me directly
              </Button>
            </a>
          </Magnetic>
        </div>
      </div>
    </div>
  );
}
